import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PlusIcon, RefreshCwIcon } from 'lucide-react';
import { ENVIRONMENT_TYPES, PERMISSIONS } from '@siteflow/shared';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { PermissionGate } from '@/components/permission-gate';
import { MaskedKey } from '@/components/sites/masked-key';
import { useSiteScope } from '@/components/sites/site-scope';
import { StatusChip } from '@/components/status-chip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { messageForError } from '@/lib/api-client';
import { siteKeys, sitesApi } from '@/lib/api/sites';
import type { EnvironmentType, SiteEnvironment } from '@/lib/api/types';
import { useOrgPermissions } from '@/lib/permissions';
import { formatDateTime } from '@/lib/utils';

function EnvironmentsTab() {
  const { orgId, siteId } = Route.useParams();
  const queryClient = useQueryClient();
  const { environments } = useSiteScope();
  const { has } = useOrgPermissions(orgId);
  const canManage = has(PERMISSIONS.SITE_ENVIRONMENTS_MANAGE);

  const [createOpen, setCreateOpen] = useState(false);
  const [newType, setNewType] = useState<EnvironmentType>('development');
  const [newName, setNewName] = useState('');
  const [newBaseUrl, setNewBaseUrl] = useState('');
  const [rotateTarget, setRotateTarget] = useState<SiteEnvironment | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { baseUrl: string; origins: string }>>({});

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: siteKeys.all(orgId) });
  }

  const draftFor = (environment: SiteEnvironment) =>
    drafts[environment.id] ?? {
      baseUrl: environment.baseUrl ?? '',
      origins: environment.allowedOrigins.join('\n'),
    };

  const updateMutation = useMutation({
    mutationFn: ({
      environmentId,
      baseUrl,
      allowedOrigins,
    }: {
      environmentId: string;
      baseUrl: string;
      allowedOrigins: string[];
    }) =>
      sitesApi.updateEnvironment(orgId, siteId, environmentId, {
        baseUrl: baseUrl.trim() || null,
        allowedOrigins,
      }),
    onSuccess: (environment) => {
      toast.success(`${environment.name} updated`);
      setDrafts((current) => {
        const next = { ...current };
        delete next[environment.id];
        return next;
      });
      invalidate();
    },
    onError: (error) => toast.error(messageForError(error, 'That environment could not be saved.')),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      sitesApi.createEnvironment(orgId, siteId, {
        type: newType,
        ...(newName ? { name: newName } : {}),
        ...(newBaseUrl ? { baseUrl: newBaseUrl } : {}),
      }),
    onSuccess: (environment) => {
      toast.success(`${environment.name} created`);
      setCreateOpen(false);
      setNewName('');
      setNewBaseUrl('');
      invalidate();
    },
    onError: (error) => toast.error(messageForError(error, 'That environment could not be created.')),
  });

  const rotateMutation = useMutation({
    mutationFn: (environmentId: string) => sitesApi.rotateKey(orgId, siteId, environmentId),
    onSuccess: (environment) => {
      toast.success(`New public key issued for ${environment.name}`);
      setRotateTarget(null);
      invalidate();
    },
    onError: (error) => toast.error(messageForError(error, 'The key could not be rotated.')),
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-prose">
          <h2 className="text-lede font-semibold tracking-tight text-ink">Environments</h2>
          <p className="text-data leading-relaxed text-ink-muted">
            One environment per deployment of the site. Each has its own public key and its own
            published content, so staging can carry copy that production has never seen.
          </p>
        </div>
        <PermissionGate permission={PERMISSIONS.SITE_ENVIRONMENTS_MANAGE}>
          <Button onClick={() => setCreateOpen(true)}>
            <PlusIcon />
            Add environment
          </Button>
        </PermissionGate>
      </div>

      <div className="flex flex-col gap-4">
        {environments.map((environment) => {
          const draft = draftFor(environment);
          const dirty =
            draft.baseUrl !== (environment.baseUrl ?? '') ||
            draft.origins !== environment.allowedOrigins.join('\n');

          return (
            <Card key={environment.id}>
              <CardHeader className="flex-row items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {environment.name}
                    <Badge variant="outline" className="font-mono">
                      {environment.type}
                    </Badge>
                  </CardTitle>
                  <p className="text-micro text-ink-subtle" data-numeric>
                    Revision {environment.contentRevision} · last published{' '}
                    {environment.lastPublishedAt
                      ? formatDateTime(environment.lastPublishedAt)
                      : 'never'}
                  </p>
                </div>
                {environment.type === 'production' ? (
                  <StatusChip tone={environment.licenceActivationId ? 'active' : 'pending'}>
                    {environment.licenceActivationId ? 'Licensed' : 'Not activated'}
                  </StatusChip>
                ) : null}
              </CardHeader>

              <CardContent className="flex flex-col gap-4">
                <MaskedKey value={environment.publicKey} label="Public key" />

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`base-${environment.id}`}>Base URL</Label>
                    <Input
                      id={`base-${environment.id}`}
                      value={draft.baseUrl}
                      disabled={!canManage}
                      placeholder="https://example.com"
                      onChange={(event) =>
                        setDrafts((current) => ({
                          ...current,
                          [environment.id]: { ...draft, baseUrl: event.target.value },
                        }))
                      }
                    />
                    <p className="text-micro leading-relaxed text-ink-subtle">
                      Where this deployment is served from. Used for the install snippet and licence
                      activation.
                    </p>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`origins-${environment.id}`}>Allowed origins</Label>
                    <Textarea
                      id={`origins-${environment.id}`}
                      rows={3}
                      value={draft.origins}
                      disabled={!canManage}
                      className="font-mono text-micro"
                      placeholder={'https://example.com\nhttps://www.example.com'}
                      onChange={(event) =>
                        setDrafts((current) => ({
                          ...current,
                          [environment.id]: { ...draft, origins: event.target.value },
                        }))
                      }
                    />
                    <p className="text-micro leading-relaxed text-ink-subtle">
                      One per line. The public content API refuses browser requests from anywhere
                      else, so a copied key cannot be used on another domain.
                    </p>
                  </div>
                </div>

                {canManage ? (
                  <div className="flex flex-wrap gap-2 border-t border-hairline pt-3">
                    <Button
                      size="sm"
                      disabled={!dirty || updateMutation.isPending}
                      onClick={() =>
                        updateMutation.mutate({
                          environmentId: environment.id,
                          baseUrl: draft.baseUrl,
                          allowedOrigins: draft.origins
                            .split('\n')
                            .map((line) => line.trim())
                            .filter(Boolean),
                        })
                      }
                    >
                      Save changes
                    </Button>
                    <Button
                      variant="outlineDanger"
                      size="sm"
                      onClick={() => setRotateTarget(environment)}
                    >
                      <RefreshCwIcon />
                      Rotate public key
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add an environment</DialogTitle>
            <DialogDescription>
              A new environment starts with no published content and its own public key. Only
              production environments consume a licence slot.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="env-type">Type</Label>
              <Select
                value={newType}
                onValueChange={(value) => setNewType(value as EnvironmentType)}
              >
                <SelectTrigger id="env-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENVIRONMENT_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="env-name">Name</Label>
              <Input
                id="env-name"
                value={newName}
                placeholder="Preview"
                onChange={(event) => setNewName(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="env-base">Base URL</Label>
              <Input
                id="env-base"
                value={newBaseUrl}
                placeholder="https://preview.example.com"
                onChange={(event) => setNewBaseUrl(event.target.value)}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button disabled={createMutation.isPending} onClick={() => createMutation.mutate()}>
              {createMutation.isPending ? 'Creating…' : 'Create environment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={rotateTarget !== null}
        onOpenChange={(open) => !open && setRotateTarget(null)}
        destructive
        title={`Rotate the public key for ${rotateTarget?.name ?? ''}?`}
        confirmLabel="Issue a new key"
        pending={rotateMutation.isPending}
        description={
          <>
            The current key stops working the moment the new one is issued. Any deployment still
            carrying the old key{' '}
            <strong className="text-ink">stops receiving content until it is redeployed</strong>{' '}
            with the replacement — the page falls back to whatever is hard-coded in its markup.
            <br />
            <br />
            Rotate when a key has leaked. Otherwise, leave it alone.
          </>
        }
        onConfirm={() => rotateTarget && rotateMutation.mutate(rotateTarget.id)}
      />
    </div>
  );
}

export const Route = createFileRoute('/app/$orgId/sites/$siteId/environments')({
  component: EnvironmentsTab,
});
